import { useEffect, useRef, useCallback } from 'react'

export interface UsePollingOptions<T> {
  /** Fonction asynchrone qui récupère les données fraîches */
  fetchFn: () => Promise<T | null>
  /** Intervalle en ms (défaut: 30000) */
  interval?: number
  /** Activé ou non (défaut: true) */
  enabled?: boolean
  /** Comparateur personnalisé — retourne true si les données ont changé */
  hasChanged?: (prev: T, next: T) => boolean
  /** Callback appelé quand un changement est détecté */
  onChanged?: (next: T, prev: T) => void
}

/**
 * Hook générique de polling silencieux.
 * 
 * - Interroge `fetchFn` à intervalle régulier
 * - Compare le résultat avec la valeur précédente via `hasChanged`
 * - Appelle `onChanged` quand des modifications sont détectées
 * - Ne déclenche aucun loader (les appels sont silencieux côté Middleware)
 *
 * @example
 * usePolling({
 *   fetchFn: () => DemandeAbsenceApi.loadDemandesAbsence(complement, undefined, true),
 *   hasChanged: (prev, next) => JSON.stringify(prev) !== JSON.stringify(next),
 *   onChanged: (next) => { setDemandes(next); notify?.info('Les données ont été mises à jour') },
 *   enabled: !!currentLogin?.identifiant,
 * })
 */
export const usePolling = <T>({
  fetchFn,
  interval = 30_000,
  enabled = true,
  hasChanged = defaultHasChanged,
  onChanged,
}: UsePollingOptions<T>) => {
  const prevDataRef = useRef<T | null>(null)
  const fetchFnRef = useRef(fetchFn)
  const hasChangedRef = useRef(hasChanged)
  const onChangedRef = useRef(onChanged)

  // Garder les refs à jour sans recréer l'intervalle
  useEffect(() => { fetchFnRef.current = fetchFn }, [fetchFn])
  useEffect(() => { hasChangedRef.current = hasChanged }, [hasChanged])
  useEffect(() => { onChangedRef.current = onChanged }, [onChanged])

  /** Permet de mettre à jour la ref interne manuellement (ex: après un chargement initial) */
  const setBaselineData = useCallback((data: T) => {
    prevDataRef.current = data
  }, [])

  useEffect(() => {
    if (!enabled) return

    const poll = async () => {
      try {
        const next = await fetchFnRef.current()
        if (next === null || next === undefined) return

        const prev = prevDataRef.current
        if (prev !== null && hasChangedRef.current(prev, next)) {
          onChangedRef.current?.(next, prev)
        }
        prevDataRef.current = next
      } catch {
        // Silencieux — pas de log pour ne pas polluer la console
      }
    }

    // Poll initial immédiat pour poser la baseline
    poll()

    const id = setInterval(poll, interval)
    return () => clearInterval(id)
  }, [enabled, interval])

  return { setBaselineData }
}

/** Comparaison par défaut via JSON.stringify (suffisant pour des structures simples) */
function defaultHasChanged<T>(prev: T, next: T): boolean {
  return JSON.stringify(prev) !== JSON.stringify(next)
}
